// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { useEffect, useState } from 'react'
import { useNavigate }          from 'react-router-dom'
import {
  ShieldCheck, AlertCircle, CheckCircle2, Clock,
  ArrowRight, RefreshCw,
} from 'lucide-react'
import { BackButton }      from '@/components/ui/BackButton'
import { Card }            from '@/components/ui/Card'
import { Badge }           from '@/components/ui/Badge'
import { Button }          from '@/components/ui/Button'
import { useAuthStore, useWizardStore } from '@/store'
import { hardResetWizard } from '@/store/wizardStore'
import { getAllModuleProgress }  from '@/services/profile'
import { loadTrustedPersons }    from '@/services/trustedPerson'
import { ROUTES, WIZARD_ROUTE } from '@/lib/constants/routes'
import { formatDateNL } from '@/lib/utils'
import type { ModuleProgress } from '@/types'

const MODULE_LABELS: Record<string, string> = {
  intake:        'Persoonlijke gegevens',
  trusted_person:'Vertrouwenspersoon',
  medical:       'Medische wensen',
  funeral:       'Uitvaartwensen',
  farewell:      'Persoonlijk afscheid',
  messages:      'Berichtenservice',
  documents:     'Documentwijzer',
  digital_estate:'Digitale nalatenschap',
  notarial:      'Notariële wensen',
}

const MODULE_ROUTES: Record<string, string> = {
  intake:        ROUTES.INTAKE,
  trusted_person:ROUTES.TRUSTED_PERSON,
  medical:       ROUTES.WISHES_MEDICAL,
  funeral:       ROUTES.WISHES_FUNERAL,
  farewell:      ROUTES.WISHES_FAREWELL,
  messages:      ROUTES.MESSAGES,
  documents:     ROUTES.DOCUMENTS,
  digital_estate:ROUTES.AI_GUIDE,
  notarial:      ROUTES.NOTARIAL,
}

export function ControlCenterPage() {
  const navigate              = useNavigate()
  const { userId }            = useAuthStore()
  const { answers, isCompleted: wizardDone, currentStep, totalSteps } = useWizardStore()
  const [modules,  setModules]  = useState<ModuleProgress[]>([])
  const [isLoading,setIsLoading]= useState(true)
  const [lastSaved, setLastSaved] = useState<string>('')

  useEffect(() => {
    if (!userId) return
    getAllModuleProgress(userId).then(async progs => {
      // Dezelfde VP-check als Dashboard: direct via Dexie voor consistente status
      const vps = await loadTrustedPersons(userId)
      const hasActiveVP = vps.filter(p => p.confirmationStatus !== 'REVOKED').length > 0

      let augmented = [...progs]
      const hasVPRecord = progs.some(p => p.moduleName === 'trusted_person' && p.status === 'COMPLETE')
      if (hasActiveVP && !hasVPRecord) {
        // Voeg een synthetisch COMPLETE-record toe zodat warnings kloppen
        augmented = [...progs, {
          id: '__vp_synthetic', userId, moduleName: 'trusted_person',
          completionPercentage: 100, stepsCurrent: 1, stepsTotal: 1,
          status: 'COMPLETE' as ModuleProgress['status'],
          startedAt: null, completedAt: null, lastVisitedAt: new Date().toISOString(),
          syncStatus: 'PENDING' as ModuleProgress['syncStatus'], cloudSyncId: null,
        }]
      }

      setModules(augmented)
      if (progs.length > 0) {
        const latest = progs.reduce((a, b) =>
          new Date(a.lastVisitedAt) > new Date(b.lastVisitedAt) ? a : b
        )
        setLastSaved(latest.lastVisitedAt)
      }
      setIsLoading(false)
    })
  }, [userId])

  const completedModules = modules.filter(m => m.status === 'COMPLETE').length

  // Warnings
  const warnings: string[] = []
  const hasTrustedPerson = modules.find(m => m.moduleName === 'trusted_person')?.status === 'COMPLETE'
  if (!hasTrustedPerson) warnings.push('Geen vertrouwenspersoon ingesteld')
  if (!wizardDone)       warnings.push(`Begeleide wizard nog niet afgerond (stap ${currentStep + 1} van ${totalSteps})`)
  if (modules.find(m => m.moduleName === 'medical')?.status === 'NOT_STARTED')
    warnings.push('Medische wensen zijn nog niet ingevuld')
  if (answers.hasChildren && modules.find(m => m.moduleName === 'intake')?.status === 'NOT_STARTED')
    warnings.push('U heeft kinderen maar voogdij is niet vastgelegd')

  return (
    <div className="flex flex-col gap-6 animate-fade-in">
      <BackButton />

      <div className="flex items-center gap-3">
        <ShieldCheck className="h-6 w-6 text-sage-600" />
        <h1 className="text-xl font-semibold text-ink">Controlecentrum</h1>
      </div>

      {/* Samenvatting */}
      <div className="grid gap-3 sm:grid-cols-2">
        <Card padding="sm" className="text-center">
          <p className="text-2xl font-bold text-ink">{completedModules}</p>
          <p className="text-xs text-ink-4 mt-1">Modules afgerond</p>
        </Card>
        <Card padding="sm" className="text-center">
          <p className="text-2xl font-bold text-ink">{warnings.length}</p>
          <p className={`text-xs mt-1 ${warnings.length > 0 ? 'text-status-warnText' : 'text-ink-4'}`}>
            Aandachtspunten
          </p>
        </Card>
      </div>

      {/* Wizard status */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-ink">Begeleide wizard</h2>
          {wizardDone
            ? <Badge variant="success">Afgerond</Badge>
            : <Badge variant="warning">Bezig</Badge>
          }
        </div>
        {!wizardDone && (
          <Button size="sm" className="mt-3" onClick={() => navigate(WIZARD_ROUTE)}>
            Wizard hervatten
          </Button>
        )}
      </Card>

      {/* Waarschuwingen */}
      {warnings.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="text-xs font-semibold text-ink-4 uppercase tracking-wider">Aandachtspunten</p>
          {warnings.map((w, i) => (
            <div key={i} className="flex items-start gap-3 rounded-xl border border-status-warnBorder bg-status-warnBg px-4 py-3">
              <AlertCircle className="h-4 w-4 text-status-warnText flex-shrink-0 mt-0.5" />
              <p className="text-sm text-status-warnText">{w}</p>
            </div>
          ))}
        </div>
      )}

      {/* Module overzicht */}
      <div>
        <p className="text-xs font-semibold text-ink-4 uppercase tracking-wider mb-3">Moduleoverzicht</p>
        {isLoading ? (
          <p className="text-sm text-ink-4">Laden...</p>
        ) : (
          <div className="flex flex-col gap-2">
            {Object.entries(MODULE_LABELS).map(([key, label]) => {
              const mod = modules.find(m => m.moduleName === key)
              const status = mod?.status ?? 'NOT_STARTED'
              return (
                <button key={key} onClick={() => navigate(MODULE_ROUTES[key] ?? '/')}
                  className="flex items-center gap-3 rounded-xl bg-white border border-sand-300 px-4 py-3 text-left hover:border-sage-300 transition-all group">
                  {status === 'COMPLETE'
                    ? <CheckCircle2 className="h-4 w-4 text-sage-600 flex-shrink-0" />
                    : <div className="h-4 w-4 rounded-full border-2 border-sand-300 flex-shrink-0" />
                  }
                  <span className="flex-1 text-sm text-ink">{label}</span>
                  <Badge
                    variant={status === 'COMPLETE' ? 'success' : status === 'IN_PROGRESS' ? 'warning' : 'muted'}
                    size="sm"
                  >
                    {status === 'COMPLETE' ? 'Klaar' : status === 'IN_PROGRESS' ? 'Bezig' : 'Te doen'}
                  </Badge>
                  <ArrowRight className="h-3.5 w-3.5 text-ink-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* Laatste opslag */}
      {lastSaved && (
        <div className="flex items-center gap-2 text-xs text-ink-4">
          <Clock className="h-3.5 w-3.5" />
          Laatst opgeslagen: {formatDateNL(lastSaved)}
        </div>
      )}

      {/* Reset */}
      <Card className="border-status-warnBorder">
        <h3 className="text-sm font-semibold text-ink mb-1 flex items-center gap-2">
          <RefreshCw className="h-4 w-4 text-status-warnText" />
          Wizard opnieuw starten
        </h3>
        <p className="text-xs text-ink-4 mb-3">Reset de wizard-voortgang. Uw ingevulde module-data blijft bewaard.</p>
        <Button
          variant="outline"
          size="sm"
          className="border-status-warnBorder text-status-warnText hover:bg-status-warnBg"
          onClick={() => { hardResetWizard(); navigate(WIZARD_ROUTE) }}
        >
          Wizard resetten
        </Button>
      </Card>
      <BackButton variant="bottom" />
    </div>
  )
}
